import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, RefreshCw, Mail, ArrowRight } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';

type VerificationState = 'loading' | 'success' | 'error' | 'expired';

interface VerifyResponse {
  success: boolean;
  message: string;
  token?: string;
  user?: {
    id: string;
    email: string;
    fullName: string | null;
    isSuperAdmin: boolean;
  };
  expiresAt?: string;
  expired?: boolean;
  alreadyVerified?: boolean;
}

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [state, setState] = useState<VerificationState>('loading');
  const [message, setMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  const { fetchUser } = useAuthStore();
  
  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage('Token not found in URL.');
      return;
    }
    
    verifyEmail();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  
  const verifyEmail = async () => {
    setState('loading');
    
    try {
      const response = await api.get<VerifyResponse>(`/auth/verify-email?token=${token}`);
      
      if (response.success && response.token) {
        // Store token
        const authData = { state: { token: response.token }, version: 0 };
        localStorage.setItem('ba-auth', JSON.stringify(authData));
        
        // Fetch user to update store
        await fetchUser();
        
        setState('success');
        setMessage(response.message || 'Email verified successfully!');
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/app');
        }, 2000);
      }
    } catch (err: unknown) {
      const error = err as { message?: string; response?: { expired?: boolean } };
      const errorMessage = error.message || 'Failed to verify email';
      
      if (errorMessage.includes('expired') || errorMessage.includes('Expired')) {
        setState('expired');
      } else {
        setState('error');
      }
      setMessage(errorMessage);
    }
  };
  
  const handleResend = async () => {
    if (!userEmail) return;
    
    setIsResending(true);
    setResendSuccess(false);
    
    try {
      await api.post('/auth/resend-verification', { email: userEmail });
      setResendSuccess(true);
    } catch {
      // Ignore errors - user will see if email arrives
    } finally {
      setIsResending(false);
    }
  };
  
  return (
    <div className="w-full max-w-md">
      <div className="bg-neutral-0 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 shadow-sm p-8 text-center">
        {/* Loading State */}
        {state === 'loading' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-accent-600 dark:text-accent-400 animate-spin" />
            </div>
            <h2 className="text-xl font-medium text-neutral-900 dark:text-neutral-100 mb-2">
              Verifying your email...
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Please wait
            </p>
          </>
        )}
        
        {/* Success State */}
        {state === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-success-50 dark:bg-success-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-success-600 dark:text-success-500" />
            </div>
            <h2 className="text-xl font-medium text-neutral-900 dark:text-neutral-100 mb-2">
              Email verified!
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
              {message}
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Redirecting to dashboard...
            </div>
          </>
        )}
        
        {/* Error State */}
        {state === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-error-50 dark:bg-error-500/20 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-error-600 dark:text-error-500" />
            </div>
            <h2 className="text-xl font-medium text-neutral-900 dark:text-neutral-100 mb-2">
              Invalid link
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
              {message || 'This link has already been used or is invalid.'}
            </p>
            <Link to="/login" className="btn btn-primary inline-flex items-center gap-2">
              Go to sign in
              <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
        
        {/* Expired State */}
        {state === 'expired' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-warning-50 dark:bg-warning-500/20 flex items-center justify-center">
              <Mail className="w-8 h-8 text-warning-600 dark:text-warning-500" />
            </div>
            <h2 className="text-xl font-medium text-neutral-900 dark:text-neutral-100 mb-2">
              Link expired
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
              This link is valid for 24 hours. Please request a new verification email.
            </p>
            
            {/* Email input for resend */}
            <div className="mb-4">
              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder="Enter your email"
                className="input w-full text-center"
              />
            </div>
            
            {resendSuccess ? (
              <div className="px-4 py-3 rounded-md text-sm flex items-center justify-center gap-2 bg-success-50 text-success-600 border border-success-500/20 mb-4">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                Email sent!
              </div>
            ) : (
              <button
                onClick={handleResend}
                disabled={isResending || !userEmail}
                className="btn btn-primary w-full mb-4"
              >
                {isResending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Send new verification email
                  </>
                )}
              </button>
            )}
            
            <div className="h-px bg-neutral-200 dark:bg-neutral-700 my-4" />
            
            <Link 
              to="/login" 
              className="text-sm text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300 font-medium"
            >
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
